import { useEffect, useState } from "react";
import { createContext } from "react";
import { gameLetters } from "../utils/letters";
import { gameWords } from "../utils/words";
import { toast } from "sonner";
import { useServerSyncTimer } from "../../../../hooks/useServerSyncTimer";

export const GameContext = createContext();

export const GameProvider = ({ children, id }) => {
  const [matchLetters, setMatchLetters] = useState([]);
  const [matchWords, setMatchWords] = useState([]);

  const [selectedLetters, setSelectedLetters] = useState([]);

  const [guessedWords, setGuessedWords] = useState([]);

  const [percentCompleted, setPercentCompleted] = useState(0);

  const [clueCounter, setClueCounter] = useState(3);
  const [clueWord, setClueWord] = useState(null);

  const [guessingAttempts, setGuessingAttempts] = useState(0);
  const [error, setError] = useState(null);

  // Initialize server-sync timer for Words Trivia
  const { timeLeft, pauseTimer, startTimer } = useServerSyncTimer(
    180, // 3 minutes
    () => {
      // Auto-submit or show timeout when time runs out
      toast.error("Time's up!");
    },
    true // Auto-start
  );

  const setMatch = () => {
    setMatchLetters([]);
    setMatchWords([]);
    setGuessedWords([]);
    setSelectedLetters([]);
    setGuessingAttempts(0);
    setClueCounter(3);
    setClueWord(null);
    setError(null);
    setPercentCompleted(0);

    // Start timer with 3 minutes using new hook
    startTimer(180);

    const randInt = Math.floor(Math.random() * 5);
    let objectLetters = [];
    gameLetters[randInt].forEach((letter, index) => {
      objectLetters.push({ id: index, value: letter });
    });

    setMatchLetters(objectLetters);

    let upperCasedWords = [];
    gameWords[randInt].forEach((word) => {
      upperCasedWords.push(word.toUpperCase());
    });

    setMatchWords(upperCasedWords);
  };

  const resetError = () => {
    setTimeout(() => {
      setError(null);
    }, 2000);
  };

  useEffect(() => {
    setMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause timer when game is completed
  useEffect(() => {
    if (percentCompleted >= 100) {
      pauseTimer();
    }
  }, [percentCompleted, pauseTimer]);

  useEffect(() => {}, [error]);

  const handleClickLetterBtn = (e) => {
    const id = Number(e.target.value);
    let letter = matchLetters.find((letter) => letter.id === id);
    if (!selectedLetters.includes(letter)) {
      setSelectedLetters([...selectedLetters, letter]);
    } else {
      const index = selectedLetters.findIndex((letter) => letter.id === id);
      const updatedSelectedLetters = [...selectedLetters];
      updatedSelectedLetters.splice(index, 1);
      setSelectedLetters(updatedSelectedLetters);
    }
  };

  const handleClickMergeLetters = () => {
    let lettersValues = [];
    selectedLetters.forEach((letter) => lettersValues.push(letter.value));
    let formedWord = lettersValues.join("");
    if (matchWords.includes(formedWord)) {
      if (guessedWords.includes(formedWord)) {
        setSelectedLetters([]);
        setError("Word already guessed!");
        resetError();
        setGuessingAttempts(guessingAttempts + 1);
      } else {
        setError(null);
        setSelectedLetters([]);
        const newGuessedWords = [...guessedWords, formedWord];
        setGuessedWords(newGuessedWords);
        setPercentCompleted(newGuessedWords.length / matchWords.length);
      }
    } else {
      setSelectedLetters([]);
      setError("Incorrect word!");
      resetError();
      setGuessingAttempts(guessingAttempts + 1);
    }
  };

  const handleClickClueBtn = () => {
    if (clueCounter > 0) {
      let randInt = Math.floor(Math.random() * matchWords.length);

      if (guessedWords.includes(matchWords[randInt])) {
        randInt -= 1;
        setClueWord(matchWords[randInt]);
      } else {
        setClueWord(matchWords[randInt]);
      }

      setClueCounter(clueCounter - 1);
    } else toast.info("No more clues left. Time to trust your instincts!");
  };

  return (
    <GameContext.Provider
      value={{
        setMatch,
        matchLetters,
        matchWords,
        selectedLetters,
        guessedWords,
        clueCounter,
        clueWord,
        guessingAttempts,
        percentCompleted,
        handleClickLetterBtn,
        handleClickMergeLetters,
        handleClickClueBtn,
        error,
        timeLeft,
        triviaId: id,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
